import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, Reply } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Emblem } from "./Emblem";

const EVT = "cast-notifications";
let cache: { unread: number; items: any[] } = { unread: 0, items: [] };

export function useNotifications(poll = false) {
  const { user } = useAuth();
  const [state, setState] = useState(cache);
  const load = useCallback(() => api.get("/notifications").then((r) => { cache = r.data; window.dispatchEvent(new Event(EVT)); }).catch(() => {}), []);
  useEffect(() => {
    const on = () => setState(cache);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  useEffect(() => {
    if (!poll || !user || !user.rules_accepted_at) return;
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 60000);
    return () => clearInterval(t);
  }, [user?.id, user?.rules_accepted_at, load, poll]); // eslint-disable-line
  const markRead = () => { if (!cache.unread) return; cache = { unread: 0, items: cache.items.map((n) => ({ ...n, read: true })) }; window.dispatchEvent(new Event(EVT)); api.post("/notifications/read").catch(() => {}); };
  return { ...state, load, markRead };
}

export function UnreadDot({ testId }: { testId: string }) {
  const { unread } = useNotifications();
  if (!unread) return null;
  return <span data-testid={testId} className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C86D51] px-1.5 text-[11px] font-bold leading-none text-white">{unread > 9 ? "9+" : unread}</span>;
}

const ago = (d: string) => { const m = Math.round((Date.now() - new Date(d).getTime()) / 60000); return m < 1 ? "just now" : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };

export function NotificationBell() {
  const { unread, items, markRead } = useNotifications(true);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) markRead(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" aria-label={`Notifications${unread ? `, ${unread} new` : ""}`} className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-[#F3ECE0]">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C86D51] px-1 text-[11px] font-bold text-white ring-2 ring-[#FAF7F2]">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] max-w-[92vw] p-0" data-testid="notifications-panel">
        <div className="border-b border-[#E2DBCE] px-4 py-3"><p className="font-semibold text-slate-900">Notifications</p></div>
        {items.length === 0 ? <p className="p-6 text-center text-sm text-slate-500" data-testid="notifications-empty">No replies yet. When someone comments on your posts or replies to you, it shows up here.</p> : (
          <ul className="max-h-[60vh] overflow-y-auto">
            {items.map((n) => (
              <li key={n.id}>
                <button data-testid={`notification-${n.id}`} onClick={() => { setOpen(false); markRead(); nav(`/cast-net?post=${n.testimony_id}`); }}
                  className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-[#FAF7F2] ${n.read ? "" : "bg-[#FBF3EC]"}`}>
                  <Emblem avatar={n.actor.avatar} size={34} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800"><b>{n.actor.display_name}</b> {n.kind === "reply" ? "replied to your comment" : "commented on your post"}</p>
                    <p className="truncate text-sm text-slate-500">“{n.snippet}”</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">{n.kind === "reply" ? <Reply className="h-3 w-3" /> : <MessageCircle className="h-3 w-3" />}{ago(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#C86D51]" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
